import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signIn, authClient } from "./auth-client";
import "./Auth.css";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Step 1: Sign in with email + password
      const result = await signIn.email({ email, password });

      // If we got a user back directly, 2FA is not enabled — go to dashboard
      if (result.data?.user && !result.data?.twoFactorRedirect) {
        navigate("/dashboard");
        return;
      }

      // If there's an error that's NOT about 2FA, show it
      if (result.error) {
        const msg = (result.error.message || "").toLowerCase();
        const code = (result.error.code || "").toLowerCase();
        const is2FAError =
          code.includes("two_factor") ||
          msg.includes("two-factor") ||
          msg.includes("2fa") ||
          msg.includes("otp");

        if (!is2FAError) {
          setError(result.error.message || "Login failed. Check your credentials.");
          setLoading(false);
          return;
        }
      }

      // Step 2: Account requires 2FA — verify the OTP code
      if (!otpCode.trim()) {
        setError("Please enter your one-time passcode from your authenticator app.");
        setLoading(false);
        return;
      }

      let verifyResult;
      if (useBackupCode) {
        verifyResult = await authClient.twoFactor.verifyBackupCode({
          code: otpCode.trim(),
        });
      } else {
        verifyResult = await authClient.twoFactor.verifyTotp({
          code: otpCode.trim(),
        });
      }

      if (verifyResult.error) {
        setError(
          verifyResult.error.message || "Invalid code. Please try again."
        );
        setOtpCode("");
        setLoading(false);
        return;
      }

      // 2FA verified — force the auth client to refresh its session state,
      // then navigate (React Router, no page reload, preserves in-memory session)
      try {
        await authClient.getSession();
      } catch {
        // getSession might not exist in all versions; that's fine
      }
      navigate("/dashboard");
    } catch (err) {
      console.error("[Login] error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>Welcome Back</h2>
          <p>Sign in to your account</p>
        </div>

        <form onSubmit={handleLogin} className="auth-form">
          {error && (
            <div className="error-message">
              <span className="error-icon">&#9888;&#65039;</span>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              className="form-input"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              className="form-input"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="otpCode">
              {useBackupCode ? "Backup Code" : "One-Time Passcode"}
            </label>
            <input
              id="otpCode"
              type="text"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              placeholder={useBackupCode ? "Enter backup code" : "123456"}
              maxLength={useBackupCode ? "20" : "6"}
              className="form-input otp-input"
              disabled={loading}
              autoComplete="one-time-code"
            />
            <small className="form-hint">
              {useBackupCode
                ? "Enter one of your saved backup codes"
                : "Enter the 6-digit code from your authenticator app"}
            </small>
            <button
              type="button"
              className="toggle-code-type"
              onClick={() => {
                setUseBackupCode(!useBackupCode);
                setOtpCode("");
              }}
              disabled={loading}
            >
              {useBackupCode
                ? "Use authenticator app instead"
                : "Use backup code instead"}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`auth-button ${loading ? "loading" : ""}`}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Signing in...
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/password-reset" className="forgot-password-link">
            Forgot your password?
          </Link>
        </div>

        <div className="auth-footer">
          <p>
            Don't have an account?{" "}
            <Link to="/register" className="auth-link">
              Sign up here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
